import React, { useState, useRef, useEffect } from 'react';
import moment from 'moment';
import 'moment-timezone';
import { FormWrapper, Label } from '../input-text/input.styled';
import { MaterialTimePickerWrapper } from './materialTimePicker.styled';
import { COLORS } from '../../constants/style/color';
import { rem } from '../../utils/style';

interface MaterialTimePickerProps {
  label?: string;
  name: string;
  id?: string;
  value: string | null;
  onChange: (name: string, value: string) => void;
  disabled?: boolean;
  required?: boolean;
  errors?: any;
  touched?: any;
  inputType?: 'View' | string;
  placeholder?: string;
  showOptional?: boolean;
  selectedDate?: Date | null;
  minTime?: string | null; // Minimum time in HH:mm format (24-hour)
  compareDate?: Date | null; // Date to compare with selectedDate for time validation
}

const MaterialTimePicker: React.FC<MaterialTimePickerProps> = ({
  label,
  name,
  id,
  value,
  onChange,
  disabled = false,
  required = false,
  errors,
  touched,
  inputType,
  placeholder = 'Select Time',
  showOptional = true,
  selectedDate = null,
  minTime = null,
  compareDate = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState<{ hour: number; minute: number; period: 'AM' | 'PM' }>({
    hour: 12,
    minute: 0,
    period: 'AM'
  });
  const timePickerRef = useRef<HTMLDivElement>(null);

  const hasError =
    (Boolean(errors) && Boolean(errors[name]) && Boolean(touched) && Boolean(touched[name]));

  // Parse current value or set to first available time
  React.useEffect(() => {
    if (value && value.trim() !== '') {
      const [time, period] = value.includes(' ') ? value.split(' ') : [value, 'AM'];
      const [hour, minute] = time.split(':');
      const hourNum = parseInt(hour);
      const minuteNum = parseInt(minute);

      if (hourNum >= 12) {
        setSelectedTime({
          hour: hourNum === 12 ? 12 : hourNum - 12,
          minute: minuteNum,
          period: 'PM'
        });
      } else {
        setSelectedTime({
          hour: hourNum === 0 ? 12 : hourNum,
          minute: minuteNum,
          period: 'AM'
        });
      }
    } else if (isOpen && selectedDate) {
      // When picker opens with no value, find and set first available time
      const currentEST = moment().tz('America/New_York');
      const selectedDateEST = moment(selectedDate).tz('America/New_York');
      const isSelectedToday = selectedDateEST.format('YYYY-MM-DD') === currentEST.format('YYYY-MM-DD');
      const isSameAsCompareDateLocal = (selectedDate && compareDate) ? (
        moment(selectedDate).format('YYYY-MM-DD') === moment(compareDate).format('YYYY-MM-DD')
      ) : false;

      // Helper to check if time is past
      const checkIfPast = (hour: number, minute: number, period: 'AM' | 'PM'): boolean => {
        let hour24 = hour;
        if (period === 'PM' && hour !== 12) {
          hour24 = hour + 12;
        } else if (period === 'AM' && hour === 12) {
          hour24 = 0;
        }

        if (isSameAsCompareDateLocal && minTime) {
          const [minHour, minMinute] = minTime.split(':').map(Number);
          if (hour24 < minHour) return true;
          if (hour24 === minHour && minute < minMinute) return true;
        }

        if (isSelectedToday) {
          const currentHour = currentEST.hours();
          const currentMinute = currentEST.minutes();
          if (hour24 < currentHour) return true;
          if (hour24 === currentHour && minute < currentMinute) return true;
        }
        return false;
      };

      let found = false;
      let hourToSet = 12;
      let minuteToSet = 0;
      let periodToSet: 'AM' | 'PM' = 'AM';

      if (isSelectedToday || isSameAsCompareDateLocal) {
        // Find first available time starting from current/minTime
        const startHour24 = isSameAsCompareDateLocal && minTime
          ? parseInt(minTime.split(':')[0])
          : currentEST.hours();
        const startMinute = isSameAsCompareDateLocal && minTime
          ? parseInt(minTime.split(':')[1])
          : currentEST.minutes();

        for (let h24 = startHour24; h24 < 24 && !found; h24++) {
          let h12 = h24 === 0 ? 12 : (h24 === 12 ? 12 : (h24 > 12 ? h24 - 12 : h24));
          let p: 'AM' | 'PM' = h24 >= 12 ? 'PM' : 'AM';
          const startM = (h24 === startHour24) ? startMinute : 0;

          for (let m = startM; m < 60 && !found; m++) {
            if (!checkIfPast(h12, m, p)) {
              hourToSet = h12;
              minuteToSet = m;
              periodToSet = p;
              found = true;
            }
          }
        }
      } else {
        // Future date, use 12:00 AM
        hourToSet = 12;
        minuteToSet = 0;
        periodToSet = 'AM';
        found = true;
      }

      if (found) {
        setSelectedTime({
          hour: hourToSet,
          minute: minuteToSet,
          period: periodToSet
        });
      }
    }
  }, [value, isOpen, selectedDate, minTime, compareDate]);

  const handleTimeSelect = (hour: number, minute: number, period: 'AM' | 'PM') => {
    // Only set if the time is not disabled (not in the past)
    if (!isTimePast(hour, minute, period)) {
      setSelectedTime({ hour, minute, period });
    }
  };

  const handleOk = () => {
    let hour24 = selectedTime.hour;
    if (selectedTime.period === 'PM' && selectedTime.hour !== 12) {
      hour24 += 12;
    } else if (selectedTime.period === 'AM' && selectedTime.hour === 12) {
      hour24 = 0;
    }

    const timeString = `${hour24.toString().padStart(2, '0')}:${selectedTime.minute.toString().padStart(2, '0')}`;
    onChange(name, timeString);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const formatDisplayTime = () => {
    if (!value || value.trim() === '') return '';

    const [time, period] = value.includes(' ') ? value.split(' ') : [value, 'AM'];
    const [hour, minute] = time.split(':');
    const hourNum = parseInt(hour);
    const minuteNum = parseInt(minute);

    if (hourNum >= 12) {
      const displayHour = hourNum === 12 ? 12 : hourNum - 12;
      return `${displayHour}:${minuteNum.toString().padStart(2, '0')} PM`;
    } else {
      const displayHour = hourNum === 0 ? 12 : hourNum;
      return `${displayHour}:${minuteNum.toString().padStart(2, '0')} AM`;
    }
  };

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  // Check if selected date is today (in EST)
  const isToday = selectedDate ? (
    moment(selectedDate).format('YYYY-MM-DD') === moment().tz('America/New_York').format('YYYY-MM-DD')
  ) : false;

  // Check if selected date equals compare date (for end time validation)
  const isSameAsCompareDate = (selectedDate && compareDate) ? (
    moment(selectedDate).format('YYYY-MM-DD') === moment(compareDate).format('YYYY-MM-DD')
  ) : false;

  // Get current time in EST
  const getCurrentESTTime = () => {
    const estMoment = moment().tz('America/New_York');
    return {
      hour: estMoment.hours(),
      minute: estMoment.minutes()
    };
  };

  // Check if a time option is in the past
  const isTimePast = (hour: number, minute: number, period: 'AM' | 'PM') => {
    // Convert to 24-hour format
    let hour24 = hour;
    if (period === 'PM' && hour !== 12) {
      hour24 = hour + 12;
    } else if (period === 'AM' && hour === 12) {
      hour24 = 0;
    }

    // Check against minimum time if same as compare date (prioritize this over isToday)
    if (isSameAsCompareDate && minTime) {
      const [minHour, minMinute] = minTime.split(':').map(Number);
      if (hour24 < minHour) return true;
      if (hour24 === minHour && minute < minMinute) return true; // Changed <= to <
    }

    // Check against current time if today (and minTime not already applied)
    if (isToday && !(isSameAsCompareDate && minTime)) {
      const currentEST = getCurrentESTTime();
      if (hour24 < currentEST.hour) return true;
      if (hour24 === currentEST.hour && minute < currentEST.minute) return true;
    }

    return false;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (timePickerRef.current && !timePickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <FormWrapper>
      {label != null && label !== '' && (
        <Label
          htmlFor={name}
          className={`${inputType === 'View' ? 'label-view' : ''}`}>
          {label} {!required && showOptional && <span>(Optional)</span>}
        </Label>
      )}
      <MaterialTimePickerWrapper ref={timePickerRef}>
        <div
          className={`time-picker-input ${hasError ? 'is-invalid' : ''} ${disabled ? 'disabled' : ''}`}
          onClick={() => !disabled && setIsOpen(true)}
        >
          <span className="time-display">
            {formatDisplayTime() || placeholder}
          </span>
          <div className="time-icons">
            {value && !disabled && (
              <span
                className="material-symbols-outlined time-clear-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(name, '');
                  setSelectedTime({ hour: 12, minute: 0, period: 'AM' });
                }}
                title="Clear time"
              >
                close
              </span>
            )}
          </div>
        </div>

        {isOpen && (
          <div className="time-picker-dropdown">
            <div className="time-picker-header">
              <span>Select Time</span>
            </div>

            <div className="time-picker-content">
              <div className="time-columns">
                <div className="time-column">
                  <div className="column-header">Hour</div>
                  <div className="time-options">
                    {hours.map(hour => {
                      // Only consider the currently selected period when determining availability
                      const periodToCheck = selectedTime.period;
                      let hasAvailableMinute = false;

                      for (let m = 0; m < 60 && !hasAvailableMinute; m++) {
                        if (!isTimePast(hour, m, periodToCheck)) {
                          hasAvailableMinute = true;
                        }
                      }

                      const isPast = !hasAvailableMinute;

                      return (
                        <div
                          key={hour}
                          className={`time-option ${selectedTime.hour === hour ? 'selected' : ''} ${isPast ? 'disabled' : ''}`}
                          onClick={() => !isPast && handleTimeSelect(hour, selectedTime.minute, selectedTime.period)}
                        >
                          {hour}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="time-column">
                  <div className="column-header">Minute</div>
                  <div className="time-options">
                    {minutes.map(minute => {
                      const isPast = isTimePast(selectedTime.hour, minute, selectedTime.period);
                      return (
                        <div
                          key={minute}
                          className={`time-option ${selectedTime.minute === minute ? 'selected' : ''} ${isPast ? 'disabled' : ''}`}
                          onClick={() => !isPast && handleTimeSelect(selectedTime.hour, minute, selectedTime.period)}
                        >
                          {minute.toString().padStart(2, '0')}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="time-column">
                  <div className="column-header">Period</div>
                  <div className="time-options">
                    {(() => {
                      // Check if AM period has any available minutes for selected hour
                      let isAMPast = true;
                      for (let m = 0; m < 60 && isAMPast; m++) {
                        if (!isTimePast(selectedTime.hour, m, 'AM')) {
                          isAMPast = false;
                        }
                      }

                      // Check if PM period has any available minutes for selected hour
                      let isPMPast = true;
                      for (let m = 0; m < 60 && isPMPast; m++) {
                        if (!isTimePast(selectedTime.hour, m, 'PM')) {
                          isPMPast = false;
                        }
                      }

                      return (
                        <>
                          <div
                            className={`time-option ${selectedTime.period === 'AM' ? 'selected' : ''} ${isAMPast ? 'disabled' : ''}`}
                            onClick={() => !isAMPast && handleTimeSelect(selectedTime.hour, selectedTime.minute, 'AM')}
                          >
                            AM
                          </div>
                          <div
                            className={`time-option ${selectedTime.period === 'PM' ? 'selected' : ''} ${isPMPast ? 'disabled' : ''}`}
                            onClick={() => !isPMPast && handleTimeSelect(selectedTime.hour, selectedTime.minute, 'PM')}
                          >
                            PM
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            <div className="time-picker-footer">
              <button
                type="button"
                className="cancel-btn"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ok-btn"
                onClick={handleOk}
                disabled={isTimePast(selectedTime.hour, selectedTime.minute, selectedTime.period)}
              >
                OK
              </button>
            </div>
          </div>
        )}
      </MaterialTimePickerWrapper>

      {hasError && errors && errors[name] && (
        <div className='error-message'>{errors[name]}</div>
      )}
    </FormWrapper>
  );
};

export default MaterialTimePicker;
